import type { Data, UID } from '@repo/strapi-types'
import { Facebook } from '@/components/icons/social/Facebook'
import { Instagram } from '@/components/icons/social/Instagram'
import { LinkedIn } from '@/components/icons/social/LinkedIn'
import { SoundCloud } from '@/components/icons/social/SoundCloud'
import { Twitter } from '@/components/icons/social/Twitter'
import { Wikipedia } from '@/components/icons/social/Wikipedia'
import { Youtube } from '@/components/icons/social/Youtube'

export const DEFAULT_REVALIDATE = 60
export const SHARED_DATA_REVALIDATE = 3600

export const FALLBACK_IMAGE_PATH = '/images/broken-image.png'
export const ROOT_PAGE_PATH = 'index'

export const SM_BREAKPOINT = 640
export const MD_BREAKPOINT = 768
export const LG_BREAKPOINT = 1024
export const XL_BREAKPOINT = 1280
export const XXL_BREAKPOINT = 1536

export const ROOT_CASE_STUDIES_PATH = '/case-studies'
export const ROOT_APPS_PATH = '/product/apps'
export const ROOT_BLOG_PAGE_PATH = '/blog'
export const SEARCH_BLOG_PAGE_PATH = [ROOT_BLOG_PAGE_PATH, 'search'].join('/')
export const AUTHOR_PAGE_PATH = [ROOT_BLOG_PAGE_PATH, 'author'].join('/')
export const CATEGORY_PAGE_PATH = [ROOT_BLOG_PAGE_PATH, 'category'].join('/')

export const PAGE_URL_PATH = '/page'

export const MOBILE_MENU_ANIMATON_DURATION = 500

// Matches Tailwind's xl breakpoint, where the full desktop navigation fits in every locale.
export const NAVBAR_DESKTOP_BREAKPOINT = 1280

// Distance between the Desktop nav bottom and blog navigation bottom border.
export const DESKTOP_SCROLL_THRESHOLD = 44

export const getAllCategories = (
  filterBy: string,
): Data.ContentType<'api::changelog-category.changelog-category'> => ({
  id: 0,
  documentId: 'all',
  name: filterBy,
  createdAt: '',
  updatedAt: '',
  publishedAt: '',
  borderColor: {
    id: 999,
    color: 'neutral-light-600',
  },
  backgroundColor: {
    id: 998,
    color: 'neutral-light-400',
  },
})

export const SOCIAL_LINKS = [
  { key: 'instagram', Icon: Instagram },
  { key: 'linkedin', Icon: LinkedIn },
  { key: 'wikipedia', Icon: Wikipedia },
  { key: 'soundcloud', Icon: SoundCloud },
  { key: 'facebook', Icon: Facebook },
  { key: 'twitter', Icon: Twitter },
  { key: 'youtube', Icon: Youtube },
] as const

export const BLOG_CONTENT_ID = 'blogContent'

export const DEFAULT_PAGE_COMPONENTS = ['global.encourage-section', 'global.icons-section'] as const

export const ARTICLES_PER_PAGE = 8

export const MARKETO_FORM_IDS = {
  BLOG_SUBSCRIPTION: 1039,
  NEWSLETTER_SUBSCRIPTION_LOKALOG: 1868,
}

export const KNOCK_AI_IDS = {
  FOOTER: 'lokalise-400a07ff-ff22-489e-b24b-f06f0580e090',
}

export const INTERCOM_WORKSPACE_ID = 'zz604pon'

export const textVariants = {
  /* Display bold variants */
  displayExtraLarge: 'lok-text-display-extra-large',
  displayLarge: 'lok-text-display-large',
  displayMedium: 'lok-text-display-medium',
  displaySmall: 'lok-text-display-small',
  /* Display medium variants */
  displayExtraLargeMedium: 'lok-text-display-extra-large-medium',
  displayLargeMedium: 'lok-text-display-large-medium',
  displayMediumMedium: 'lok-text-display-medium-medium',
  displaySmallMedium: 'lok-text-display-small-medium',
  /* Heading bold variants */
  headingLarge: 'lok-text-heading-large',
  headingMedium: 'lok-text-heading-medium',
  headingSmall: 'lok-text-heading-small',
  /* Heading medium variants */
  headingLargeMedium: 'lok-text-heading-large-medium',
  headingMediumMedium: 'lok-text-heading-medium-medium',
  headingSmallMedium: 'lok-text-heading-small-medium',
  headingSmallLight: 'lok-text-heading-small-light',
  headingSubtitle: 'lok-text-heading-subtitle',
  headingPricingTitle: 'lok-text-heading-pricing-title',
  /* Body bold variants */
  bodyBoldExtraLarge: 'lok-text-body-bold-extra-large',
  bodyBoldLarge: 'lok-text-body-bold-large',
  bodyBoldMedium: 'lok-text-body-bold-medium',
  bodyBoldSmall: 'lok-text-body-bold-small',
  bodyBoldExtraSmall: 'lok-text-body-bold-extra-small',
  /* Body regular variants */
  bodyRegularExtraLarge: 'lok-text-body-regular-extra-large',
  bodyRegularLarge: 'lok-text-body-regular-large',
  bodyRegularMedium: 'lok-text-body-regular-medium',
  bodyRegularSmall: 'lok-text-body-regular-small',
  bodyRegularExtraSmall: 'lok-text-body-regular-extra-small',
  /* Serif variants */
  serifExtraLarge: 'lok-text-serif-extra-large',
  serifLarge: 'lok-text-serif-large',
  serifMedium: 'lok-text-serif-medium',
  serifSmall: 'lok-text-serif-small',
}

export const ckEditorTextVariants = {
  /* Display variants */
  displayExtraLarge: 'lok-text-display-extra-large',
  displayLarge: 'lok-text-display-large',
  displayMedium: 'lok-text-display-medium',
  displaySmall: 'lok-text-display-small',
  /* Display medium variants */
  displayExtraLargeMedium: 'lok-text-display-extra-large-medium',
  displayLargeMedium: 'lok-text-display-large-medium',
  displayMediumMedium: 'lok-text-display-medium-medium',
  displaySmallMedium: 'lok-text-display-small-medium',
  /* Heading variants */
  headingLarge: 'lok-text-heading-large',
  headingMedium: 'lok-text-heading-medium',
  headingSmall: 'lok-text-heading-small',
  headingSmallLight: 'lok-text-heading-small-light',
  headingSubtitle: 'lok-text-heading-subtitle',
  headingPricingTitle: 'lok-text-heading-pricing-title',
  /* Body variants */
  bodyRegularExtraLarge: 'lok-text-body-regular-extra-large',
  bodyLarge: 'lok-text-body-large',
  bodyMedium: 'lok-text-body-medium',
  bodySmall: 'lok-text-body-small',
  bodyExtraSmall: 'lok-text-body-extra-small',
  /* Serif variants */
  serifExtraLarge: 'lok-text-serif-extra-large',
  serifLarge: 'lok-text-serif-large',
  serifMedium: 'lok-text-serif-medium',
  serifSmall: 'lok-text-serif-small',
}

export const textColorVariants = {
  black: 'text-brand-black',
  salmon: 'text-brand-salmon',
  white: 'text-neutral-light-100',
  gray: 'text-neutral-light-alpha-500',
  link: 'text-brand-black hover:text-brand-salmon duration-200 transition-colors',
}

export const GOOGLE_ADS_CLICK_ID_PARAM = 'gclid'

export const CLICK_ID_PARAMS = [
  GOOGLE_ADS_CLICK_ID_PARAM,
  'li_fat_id', // LinkedIn click ID
] as const

export const industryKeys = [
  'saasAndTechnology',
  'finance',
  'healthcare',
  'education',
  'foodAndBeverage',
  'entertainment',
  'eSports',
  'agriculture',
  'marketingAndAdvertising',
  'retailAndECommerce',
  'telecommunication',
  'transportation',
  'software',
  'humanResources',
  'electronics',
  'other',
] as const

export const companySizeKeys = ['small', 'midSize', 'enterprise'] as const

export const languageKeys = [
  'afrikaans',
  'akan',
  'arabic',
  'armenian',
  'azerbaijani',
  'basque',
  'belarusian',
  'bengali',
  'bosnian',
  'bulgarian',
  'burmese',
  'catalan',
  'chamorro',
  'chinese_simplified',
  'chinese_traditional',
  'croatian',
  'czech',
  'danish',
  'dutch',
  'english_ca',
  'english_uk',
  'english_us',
  'english_aus',
  'english_se',
  'estonian',
  'faroese',
  'filipino',
  'finnish',
  'french_can',
  'french_fr',
  'gabonese',
  'galician',
  'georgian',
  'german',
  'greek',
  'gujarati',
  'haitian_creole',
  'hawaiian',
  'hebrew',
  'hindi',
  'hungarian',
  'igbo',
  'indonesian',
  'irish',
  'italian',
  'japanese',
  'javanese',
  'kannada',
  'kazakh',
  'khmer',
  'kikuyu',
  'korean',
  'lao',
  'latvian',
  'lithuanian',
  'luxembourgish',
  'macedonian',
  'malay',
  'maltese',
  'maori',
  'marathi',
  'marshallese',
  'mongolian',
  'nepali',
  'norwegian',
  'oriya',
  'pashto',
  'persian',
  'polish',
  'portuguese_br',
  'portuguese_pt',
  'punjabi',
  'romanian',
  'russian',
  'serbian',
  'sindhi',
  'sinhala',
  'slovak',
  'somali',
  'spanish_mex',
  'spanish_esp',
  'sundanese',
  'swahili',
  'swedish',
  'tagalog',
  'tamil',
  'telugu',
  'thai',
  'tigrinya',
  'tongan',
  'turkish',
  'ukrainian',
  'urdu',
  'uzbek',
  'vietnamese',
  'welsh',
  'xhosa',
  'yoruba',
  'zulu',
] as const

export type LanguageKey = (typeof languageKeys)[number]

export const HERO_COMPONENTS: UID.Component[] = ['global.centered-banner', 'global.hero']

export const FULL_BACKGROUND_COMPONENTS: UID.Component[] = [
  'global.benefit-cards',
  'global.cards-banner-ai',
  'global.comparison-cards',
  'global.encourage-section',
  'global.feature-block',
  'global.feature-tabset',
  'global.icons-section',
  'global.scenario-cards',
  'global.text-columns',
  'global.pricing-v1',
]

export const defaultRobots = 'noindex,nofollow'

// Constant used to map `meta.robots` values from Strapi to Next.js compatible format (In Strapi stored in enum in seo components.)
export const metaRobots: Record<
  NonNullable<Data.Component<'shared.seo'>['metaRobots']> | string,
  Record<string, boolean | number | string>
> = {
  all: {
    index: true,
    follow: true,
  },
  index: {
    index: true,
  },
  'index,follow': {
    index: true,
    follow: true,
  },
  noindex: {
    index: false,
  },
  'noindex,follow': {
    index: false,
    follow: true,
  },
  'noindex,nofollow': {
    index: false,
    follow: false,
  },
  none: {},
  noarchive: {
    noarchive: true,
  },
  nosnippet: {
    nosnippet: true,
  },
  'max-snippet': {
    'max-snippet': 1,
  },
}

export const defaultMetaRobots: Record<string, boolean | number | string> = {
  'max-image-preview': 'large',
}
